import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Video, X, ArrowRight, Clock, Settings2, AlertTriangle, Info, CheckCircle2, AlertCircle, Megaphone } from 'lucide-react';
import { useActiveInterviewSessions } from '@/hooks/useActiveInterviewSessions';
import { useUnconfiguredInterviewJobs } from '@/hooks/useUnconfiguredInterviewJobs';
import { useAdminBanners } from '@/hooks/useAdminBanners';
import { useServiceGovernance } from '@/hooks/useServiceGovernance';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { validateAdminBannerNavigationTarget } from '@/lib/adminBannerNavigation';
import { useTranslation } from 'react-i18next';

interface BannerItem {
  id: string;
  icon: typeof Video;
  title: string;
  description: string;
  linkTo?: string;
  linkLabel?: string;
  accentColor: 'warning' | 'destructive' | 'info' | 'success';
  badge: string;
  persistDismiss?: boolean; // if true, dismiss is saved to DB
}

const ROTATE_INTERVAL = 5000;

export function ActiveInterviewsBanner() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { data: pendingCount, isLoading: pendingLoading } = useActiveInterviewSessions();
  const { data: unconfiguredCount, isLoading: unconfiguredLoading } = useUnconfiguredInterviewJobs();
  const interviewGovernance = useServiceGovernance('ai_interviews');
  const hasAiInterviews = interviewGovernance.allowed;
  const serviceLoading = interviewGovernance.isLoading;
  const { data: adminData, isLoading: adminLoading } = useAdminBanners('hire');

  const [localDismissedIds, setLocalDismissedIds] = useState<Set<string>>(new Set());
  const [activeIndex, setActiveIndex] = useState(0);

  const isLoading = pendingLoading || unconfiguredLoading || serviceLoading || adminLoading;
  const overrideSystem = adminData?.overrideSystem ?? false;

  // Build banner items
  const allBanners = useMemo(() => {
    const items: BannerItem[] = [];

    // Admin banners first (highest priority)
    if (adminData?.banners) {
      for (const ab of adminData.banners) {
        const linkValidation = validateAdminBannerNavigationTarget(ab.link_url);
        const linkTo = linkValidation.valid ? linkValidation.target : null;
        const severityMap: Record<string, BannerItem['accentColor']> = {
          info: 'info', warning: 'warning', critical: 'destructive', success: 'success',
        };
        items.push({
          id: `admin-${ab.id}`,
          icon: ab.severity === 'critical' ? AlertCircle : ab.severity === 'warning' ? AlertTriangle : ab.severity === 'success' ? CheckCircle2 : Megaphone,
          title: ab.title,
          description: ab.body || '',
          linkTo: linkTo || undefined,
          linkLabel: linkTo ? ab.link_label || undefined : undefined,
          accentColor: severityMap[ab.severity] || 'info',
          badge: ab.severity === 'critical'
            ? t('hire.activeInterviewsBanner.badges.urgent')
            : ab.severity === 'warning'
              ? t('hire.activeInterviewsBanner.badges.notice')
              : ab.severity === 'success'
                ? t('hire.activeInterviewsBanner.badges.update')
                : t('hire.activeInterviewsBanner.badges.announcement'),
          persistDismiss: ab.is_dismissible,
        });
      }
    }

    // System banners (only if not overridden by admin)
    if (!overrideSystem) {
      if (hasAiInterviews && unconfiguredCount && unconfiguredCount > 0) {
        items.push({
          id: 'unconfigured-questions',
          icon: Settings2,
          title: unconfiguredCount === 1
            ? t('hire.activeInterviewsBanner.unconfigured.titleOne')
            : t('hire.activeInterviewsBanner.unconfigured.titleOther', { count: unconfiguredCount }),
          description: unconfiguredCount === 1
            ? t('hire.activeInterviewsBanner.unconfigured.descriptionOne')
            : t('hire.activeInterviewsBanner.unconfigured.descriptionOther', { count: unconfiguredCount }),
          linkTo: '/interviews',
          linkLabel: t('hire.activeInterviewsBanner.configure'),
          accentColor: 'destructive',
          badge: t('hire.activeInterviewsBanner.badges.actionRequired'),
        });
      }

      if (pendingCount && pendingCount > 0) {
        items.push({
          id: 'pending-sessions',
          icon: Video,
          title: pendingCount === 1
            ? t('hire.activeInterviewsBanner.pending.titleOne')
            : t('hire.activeInterviewsBanner.pending.titleOther', { count: pendingCount }),
          description: pendingCount === 1
            ? t('hire.activeInterviewsBanner.pending.descriptionOne')
            : t('hire.activeInterviewsBanner.pending.descriptionOther', { count: pendingCount }),
          linkTo: '/interviews',
          linkLabel: t('hire.activeInterviewsBanner.review'),
          accentColor: 'warning',
          badge: t('hire.activeInterviewsBanner.badges.awaiting'),
        });
      }
    }

    return items;
  }, [hasAiInterviews, unconfiguredCount, pendingCount, adminData, overrideSystem, t]);

  // Filter dismissed
  const visibleBanners = useMemo(
    () => allBanners.filter(b => !localDismissedIds.has(b.id)),
    [allBanners, localDismissedIds]
  );

  // Auto-rotate
  useEffect(() => {
    if (visibleBanners.length <= 1) return;
    const timer = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % visibleBanners.length);
    }, ROTATE_INTERVAL);
    return () => clearInterval(timer);
  }, [visibleBanners.length]);

  useEffect(() => {
    if (activeIndex >= visibleBanners.length) setActiveIndex(0);
  }, [activeIndex, visibleBanners.length]);

  const handleDismiss = useCallback(async (banner: BannerItem) => {
    setLocalDismissedIds(prev => new Set(prev).add(banner.id));

    // Persist admin banner dismissals to DB
    if (banner.persistDismiss && banner.id.startsWith('admin-') && session?.user?.id) {
      const realId = banner.id.replace('admin-', '');
      await supabase.from('admin_banner_dismissals').insert({
        banner_id: realId,
        user_id: session.user.id,
      }).then(() => {});
    }
  }, [session?.user?.id]);

  if (isLoading || visibleBanners.length === 0) return null;

  const current = visibleBanners[activeIndex];
  if (!current) return null;

  const colorMap = {
    warning: {
      border: 'border-warning/30',
      bar: 'bg-warning',
      iconBg: 'bg-warning/15',
      iconRing: 'ring-warning/5',
      iconText: 'text-warning',
      dotBg: 'bg-warning',
      badgeBg: 'bg-warning/15',
      badgeText: 'text-warning',
      btnClass: 'bg-warning text-warning-foreground hover:bg-warning/90',
      gradientFrom: 'from-warning/8',
    },
    destructive: {
      border: 'border-destructive/30',
      bar: 'bg-destructive',
      iconBg: 'bg-destructive/15',
      iconRing: 'ring-destructive/5',
      iconText: 'text-destructive',
      dotBg: 'bg-destructive',
      badgeBg: 'bg-destructive/15',
      badgeText: 'text-destructive',
      btnClass: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
      gradientFrom: 'from-destructive/8',
    },
    info: {
      border: 'border-primary/30',
      bar: 'bg-primary',
      iconBg: 'bg-primary/15',
      iconRing: 'ring-primary/5',
      iconText: 'text-primary',
      dotBg: 'bg-primary',
      badgeBg: 'bg-primary/15',
      badgeText: 'text-primary',
      btnClass: 'bg-primary text-primary-foreground hover:bg-primary/90',
      gradientFrom: 'from-primary/8',
    },
    success: {
      border: 'border-success/30',
      bar: 'bg-success',
      iconBg: 'bg-success/15',
      iconRing: 'ring-success/5',
      iconText: 'text-success',
      dotBg: 'bg-success',
      badgeBg: 'bg-success/15',
      badgeText: 'text-success',
      btnClass: 'bg-success text-success-foreground hover:bg-success/90',
      gradientFrom: 'from-success/8',
    },
  };

  const colors = colorMap[current.accentColor];
  const IconComponent = current.icon;

  return (
    <div
      key={current.id}
      className={cn(
        'relative overflow-hidden rounded-xl border shadow-sm transition-all duration-300',
        colors.border,
        `bg-gradient-to-r ${colors.gradientFrom} via-transparent to-transparent`
      )}
    >
      <div className={cn('absolute inset-y-0 start-0 w-1', colors.bar)} />

      <div className="px-4 py-3 ps-5 sm:px-5 sm:py-3.5 sm:ps-6">
        <div className="flex items-start sm:items-center gap-3 sm:gap-4">
          <div className={cn(
            'relative flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full ring-4 shrink-0 mt-0.5 sm:mt-0',
            colors.iconBg, colors.iconRing
          )}>
            <IconComponent className={cn('w-3.5 h-3.5 sm:w-4.5 sm:h-4.5', colors.iconText)} />
            <span className={cn(
              'absolute -top-0.5 -end-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ring-2 ring-background animate-pulse',
              colors.dotBg
            )} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start sm:items-center gap-1.5 sm:gap-2 flex-wrap">
              <p className="font-semibold text-xs sm:text-sm text-foreground leading-tight">{current.title}</p>
              <span className={cn(
                'inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-wider',
                colors.badgeBg, colors.badgeText
              )}>
                {current.accentColor === 'destructive'
                  ? <AlertTriangle className="w-2.5 h-2.5" />
                  : current.accentColor === 'success'
                    ? <CheckCircle2 className="w-2.5 h-2.5" />
                    : <Clock className="w-2.5 h-2.5" />}
                {current.badge}
              </span>
            </div>
            {current.description && (
              <p className="hidden sm:block text-[11px] sm:text-xs text-muted-foreground mt-0.5 line-clamp-2">{current.description}</p>
            )}
          </div>

          {/* Dismiss */}
          <button
            onClick={() => handleDismiss(current)}
            className="p-1 sm:p-1.5 rounded-full text-destructive/60 hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
            aria-label={t('common.dismiss')}
          >
            <X className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          </button>
        </div>

        {/* Bottom row: dots + action */}
        {(current.linkTo || visibleBanners.length > 1) && (
          <div className="flex items-center justify-between mt-2 sm:mt-0 sm:absolute sm:end-14 sm:top-1/2 sm:-translate-y-1/2 gap-3 ps-11 sm:ps-0">
            {visibleBanners.length > 1 && (
              <div className="flex items-center gap-1.5 shrink-0">
                {visibleBanners.map((b, i) => (
                  <button
                    key={b.id}
                    onClick={() => setActiveIndex(i)}
                    className={cn(
                      'w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all duration-200',
                      i === activeIndex
                        ? cn('w-4 sm:w-5', colors.bar)
                        : 'bg-muted-foreground/20 hover:bg-muted-foreground/40'
                    )}
                    aria-label={t('hire.activeInterviewsBanner.showBanner', { index: i + 1 })}
                  />
                ))}
              </div>
            )}
            {current.linkTo && (
              <Link to={current.linkTo} className="shrink-0">
                <Button
                  size="sm"
                  className={cn('rounded-full gap-1 text-xs h-7 px-3 shadow-sm font-medium', colors.btnClass)}
                >
                  {current.linkLabel || t('hire.activeInterviewsBanner.view')} <ArrowRight className="w-3 h-3 rtl-flip" />
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
